import { Loader } from "@googlemaps/js-api-loader";
import React, { useEffect, useRef, useState } from "react";
import { createLogger } from "../../utils/logger";

const logger = createLogger("CapacitorSafeAddressInput");

/**
 * Address input using Google Maps Places API (google.maps.places.Autocomplete).
 * Replaces react-google-places-autocomplete with native Places API.
 * Same interface: apiKey, selectProps (placeholder, value, onChange, styles), apiOptions.
 * See: https://developers.google.com/maps/documentation/javascript/place-autocomplete
 */
const CapacitorSafeAddressInput = ({
  apiKey,
  selectProps = {},
  apiOptions = {},
  ...props
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const onChangeRef = useRef(selectProps?.onChange);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [inputValue, setInputValue] = useState(selectProps?.value?.label ?? "");

  // Keep ref updated - avoids useEffect re-running on every parent render (fixes iOS form reset)
  onChangeRef.current = selectProps?.onChange;

  useEffect(() => {
    if (!apiKey) return;

    const initAutocomplete = () => {
      if (!inputRef.current || !window.google?.maps?.places) return;
      try {
        const Autocomplete = window.google.maps.places.Autocomplete;
        autocompleteRef.current = new Autocomplete(inputRef.current, {
          types: ["address"],
          fields: ["formatted_address", "place_id", "address_components"],
          ...apiOptions
        });
        autocompleteRef.current.addListener("place_changed", () => {
          const place = autocompleteRef.current?.getPlace();
          const label = place?.formatted_address || "";
          if (label && onChangeRef.current) {
            onChangeRef.current({
              label,
              value: {
                description: label,
                place_id: place.place_id,
                structured_formatting: { main_text: label }
              }
            });
          }
        });
        setLoaded(true);
      } catch (err) {
        logger.error("Places Autocomplete init error:", err);
        setError(err?.message);
      }
    };

    if (window.google?.maps?.places) {
      initAutocomplete();
      return () => {
        autocompleteRef.current = null;
      };
    }

    const loader = new Loader({
      apiKey,
      version: "weekly",
      libraries: ["places"]
    });
    loader
      .load()
      .then(initAutocomplete)
      .catch((err) => {
        logger.error("Failed to load Google Places:", err);
        setError(err?.message || "Failed to load");
      });

    return () => {
      autocompleteRef.current = null;
    };
  }, [apiKey, apiOptions]);

  const placeholder = selectProps?.placeholder ?? "Enter address...";
  let controlStyle = {};
  try {
    if (typeof selectProps?.styles?.control === "function") {
      controlStyle = selectProps.styles.control() || {};
    }
  } catch (_) {}

  const inputStyle = {
    width: "100%",
    padding: "12px 16px",
    border: "1px solid #ddd",
    borderRadius: "8px",
    fontSize: "15px",
    outline: "none",
    boxSizing: "border-box",
    ...controlStyle
  };

  if (error) {
    return (
      <input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => {
          const v = e.target.value;
          setInputValue(v);
          selectProps?.onChange?.({
            label: v,
            value: {
              description: v,
              structured_formatting: { main_text: v }
            }
          });
        }}
        style={inputStyle}
      />
    );
  }

  return (
    <input
      ref={inputRef}
      type="text"
      placeholder={loaded ? placeholder : "Loading..."}
      defaultValue={selectProps?.value?.label ?? ""}
      style={inputStyle}
    />
  );
};

export default CapacitorSafeAddressInput;
